import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { logError } from "@/lib/logger";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
    Gift,
    Wallet,
    Check,
    Sliders,
    FloppyDisk,
} from "@phosphor-icons/react";

const CURRENCY_SYMBOL = { INR: "₹", USD: "$", EUR: "€", GBP: "£" };

export default function ReferralPayouts() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [data, setData] = useState(null);
    const [statusFilter, setStatusFilter] = useState("all");
    const [busy, setBusy] = useState(null);
    const [settings, setSettings] = useState(null);
    const [amountDraft, setAmountDraft] = useState("");
    const [interviewAmountDraft, setInterviewAmountDraft] = useState("");
    const [interviewEnabledDraft, setInterviewEnabledDraft] = useState(true);
    const [savingSettings, setSavingSettings] = useState(false);

    const load = useCallback(() => {
        const q = statusFilter === "all" ? "" : `?status=${statusFilter}`;
        Promise.all([
            api.get(`/referrals/payouts${q}`),
            api.get("/settings/referral"),
        ])
            .then(([r, s]) => {
                setData(r.data);
                setSettings(s.data);
                setAmountDraft(String(s.data.amount || ""));
                setInterviewAmountDraft(String(s.data.interview_amount ?? 100));
                setInterviewEnabledDraft(s.data.interview_enabled !== false);
            })
            .catch((e) => logError("Failed to load payouts:", e));
    }, [statusFilter]);

    useEffect(() => { load(); }, [load]);

    const markPaid = async (id) => {
        setBusy(id);
        try {
            await api.post(`/referrals/payouts/${id}/mark-paid`);
            toast.success("Payout marked as paid");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not mark as paid");
        } finally {
            setBusy(null);
        }
    };

    const saveSettings = async () => {
        setSavingSettings(true);
        try {
            const amt = parseFloat(amountDraft);
            const iAmt = parseFloat(interviewAmountDraft);
            if (isNaN(amt) || amt < 0) throw new Error("Enter a valid hired bounty amount");
            if (isNaN(iAmt) || iAmt < 0) throw new Error("Enter a valid interview bounty amount");
            await api.put("/settings/referral", {
                amount: amt,
                currency: settings.currency || "INR",
                interview_amount: iAmt,
                interview_enabled: interviewEnabledDraft,
            });
            toast.success("Referral bounty updated");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message || "Failed to save");
        } finally {
            setSavingSettings(false);
        }
    };

    if (!data || !settings) return <div className="p-10 text-[#525252]">Loading…</div>;

    const symbol = CURRENCY_SYMBOL[data.totals.currency] || data.totals.currency + " ";

    return (
        <div className="px-4 sm:px-8 py-6 sm:py-10 max-w-[1400px] mx-auto" data-testid="referral-payouts-page">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
                <div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">
                        Referral bounty program
                    </p>
                    <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter text-ink">
                        Referral payouts.
                    </h1>
                </div>
                <div className="flex gap-2 self-start sm:self-auto">
                    {["all", "pending", "paid"].map((s) => (
                        <button
                            key={s}
                            onClick={() => setStatusFilter(s)}
                            data-testid={`payout-filter-${s}`}
                            className={`px-3 py-2 text-[11px] font-bold uppercase tracking-wider transition-colors ${
                                statusFilter === s
                                    ? "bg-ink text-white"
                                    : "bg-white border border-[#E5E5E5] text-[#525252] hover:border-ink"
                            }`}
                        >
                            {s}
                        </button>
                    ))}
                </div>
            </div>

            {/* Totals + settings */}
            <div className="grid md:grid-cols-4 gap-3 sm:gap-4 mb-8">
                <div className="bg-white border border-[#E5E5E5] p-5" data-testid="payout-total-pending">
                    <div className="flex items-center gap-2 mb-2">
                        <Wallet size={14} weight="bold" className="text-[#B45309]" />
                        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#525252]">Pending</p>
                    </div>
                    <p className="font-heading font-black text-3xl sm:text-4xl tracking-tighter text-[#B45309]">
                        {symbol}{data.totals.pending.toLocaleString("en-IN")}
                    </p>
                </div>
                <div className="bg-white border border-[#E5E5E5] p-5" data-testid="payout-total-paid">
                    <div className="flex items-center gap-2 mb-2">
                        <Check size={14} weight="bold" className="text-[#059669]" />
                        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#525252]">Paid</p>
                    </div>
                    <p className="font-heading font-black text-3xl sm:text-4xl tracking-tighter text-[#059669]">
                        {symbol}{data.totals.paid.toLocaleString("en-IN")}
                    </p>
                </div>
                <div className="bg-white border border-[#E5E5E5] p-5" data-testid="payout-settings-card">
                    <div className="flex items-center gap-2 mb-2">
                        <Sliders size={14} weight="bold" className="text-cobalt" />
                        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#525252]">
                            Bounty per hire
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="font-heading font-black text-2xl tracking-tighter text-ink">{symbol}</span>
                        <input
                            type="number"
                            step="100"
                            min="0"
                            disabled={!isAdmin}
                            value={amountDraft}
                            onChange={(e) => setAmountDraft(e.target.value)}
                            data-testid="payout-settings-amount"
                            className="font-heading font-black text-2xl tracking-tighter text-ink bg-transparent border-b border-[#E5E5E5] focus:border-cobalt focus:outline-none w-full disabled:opacity-70"
                        />
                        {isAdmin && (
                            <button
                                onClick={saveSettings}
                                disabled={savingSettings || amountDraft === String(settings.amount)}
                                data-testid="payout-settings-save"
                                className="bg-ink text-white px-3 py-2 text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1 disabled:opacity-40"
                            >
                                <FloppyDisk size={12} weight="bold" /> Save
                            </button>
                        )}
                    </div>
                    <p className="text-[11px] text-[#A3A3A3] mt-2">
                        {isAdmin
                            ? "Applies to all future referral hires"
                            : "Ask admin to update the bounty amount"}
                    </p>
                </div>

                {/* Batch 3 (Feb 2026) — Interview-stage engagement bounty */}
                <div className="bg-white border border-[#E5E5E5] p-4 sm:p-5" data-testid="settings-interview-bounty">
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3] mb-2 inline-flex items-center gap-1.5">
                        <Sliders size={11} weight="bold" /> Interview-stage bounty
                    </p>
                    <div className="flex items-center gap-2">
                        <span className="font-heading font-black text-2xl tracking-tighter text-ink">{symbol}</span>
                        <input
                            type="number"
                            step="10"
                            min="0"
                            disabled={!isAdmin}
                            value={interviewAmountDraft}
                            onChange={(e) => setInterviewAmountDraft(e.target.value)}
                            data-testid="settings-interview-amount"
                            className="font-heading font-black text-2xl tracking-tighter text-ink bg-transparent border-b border-[#E5E5E5] focus:border-cobalt focus:outline-none w-full disabled:opacity-70"
                        />
                    </div>
                    {isAdmin && (
                        <label className="flex items-center gap-2 mt-2 text-xs text-[#525252] cursor-pointer">
                            <input
                                type="checkbox"
                                checked={interviewEnabledDraft}
                                onChange={(e) => setInterviewEnabledDraft(e.target.checked)}
                                data-testid="settings-interview-enabled"
                            />
                            Enabled
                        </label>
                    )}
                    <p className="text-[11px] text-[#A3A3A3] mt-2">
                        Small engagement bounty when a referred candidate first
                        reaches the Interview stage. Recommended: ₹50–₹100.
                    </p>
                </div>
            </div>

            {/* Payouts list */}
            {data.items.length === 0 ? (
                <div className="bg-white border border-[#E5E5E5] p-8 text-center" data-testid="payout-empty">
                    <Gift size={28} weight="bold" className="text-[#A3A3A3] mx-auto mb-3" />
                    <p className="text-sm text-[#525252]">
                        No {statusFilter !== "all" ? statusFilter : ""} payouts yet — candidates need to refer someone who reaches the Hired stage.
                    </p>
                </div>
            ) : (
                <>
                {/* Mobile cards */}
                <div className="sm:hidden space-y-3" data-testid="payout-cards">
                    {data.items.map((p) => {
                        const sym = CURRENCY_SYMBOL[p.currency] || p.currency + " ";
                        return (
                            <div key={`m-${p.id}`} data-testid={`payout-card-${p.id}`} className="bg-white border border-[#E5E5E5] p-4">
                                <div className="flex items-start justify-between gap-2 mb-2">
                                    <div className="min-w-0">
                                        <p className="font-bold text-ink truncate">{p.referrer?.name || "—"}</p>
                                        <p className="text-[10px] text-[#A3A3A3]">Referrer · {p.referrer_code}</p>
                                    </div>
                                    <span
                                        className={`text-[10px] font-black uppercase tracking-wider px-2 py-1 shrink-0 ${
                                            p.status === "paid"
                                                ? "bg-[#ECFDF5] text-[#059669]"
                                                : "bg-[#FEF3C7] text-[#B45309]"
                                        }`}
                                    >
                                        {p.status}
                                    </span>
                                </div>
                                <div className="pt-2 border-t border-[#F5F5F5] space-y-2 text-xs">
                                    <div>
                                        <p className="text-[9px] uppercase tracking-wider text-[#A3A3A3]">Hired candidate</p>
                                        <p className="text-ink">{p.referred?.name || "—"}</p>
                                        <p className="text-[10px] text-[#A3A3A3] truncate">{p.referred?.email}</p>
                                    </div>
                                    <div>
                                        <p className="text-[9px] uppercase tracking-wider text-[#A3A3A3]">Role</p>
                                        <p className="text-ink truncate">{p.job?.title || "—"}</p>
                                        {p.job?.code && (
                                            <span className="mt-0.5 font-mono text-[10px] tracking-wider bg-[#F5F5F5] text-[#525252] px-1.5 py-0.5 inline-block">
                                                {p.job.code}
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center justify-between pt-1">
                                        <div>
                                            <p className="font-heading font-black text-ink">{sym}{p.amount.toLocaleString("en-IN")}</p>
                                            <span className={`text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 ${
                                                p.amount_source === "job"
                                                    ? "bg-gradient-to-r from-[#FFFBEB] to-[#FEF3C7] text-[#92400E] border border-[#F59E0B]/50"
                                                    : "bg-[#F5F5F5] text-[#525252] border border-[#E5E5E5]"
                                            }`}>
                                                {p.amount_source === "job" ? "Per-job" : "Workspace"}
                                            </span>
                                        </div>
                                        {p.status === "pending" ? (
                                            <button
                                                onClick={() => markPaid(p.id)}
                                                disabled={busy === p.id}
                                                data-testid={`payout-mark-paid-card-${p.id}`}
                                                className="btn-gradient px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1 disabled:opacity-50"
                                            >
                                                <Check size={12} weight="bold" /> Mark paid
                                            </button>
                                        ) : (
                                            <span className="text-[10px] text-[#A3A3A3]">
                                                {p.paid_at && new Date(p.paid_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
                {/* Desktop table */}
                <div className="hidden sm:block bg-white border border-[#E5E5E5] overflow-x-auto" data-testid="payout-table">
                    <table className="w-full text-sm">
                        <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                            <tr className="text-left text-[10px] font-black uppercase tracking-[0.15em] text-[#525252]">
                                <th className="px-4 py-3">Referrer</th>
                                <th className="px-4 py-3">Hired candidate</th>
                                <th className="px-4 py-3">Role</th>
                                <th className="px-4 py-3">Amount</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[#F5F5F5]">
                            {data.items.map((p) => {
                                const sym = CURRENCY_SYMBOL[p.currency] || p.currency + " ";
                                return (
                                    <tr key={p.id} data-testid={`payout-row-${p.id}`}>
                                        <td className="px-4 py-3">
                                            <p className="font-bold text-ink">{p.referrer?.name || "—"}</p>
                                            <p className="text-[11px] text-[#A3A3A3]">{p.referrer_code}</p>
                                        </td>
                                        <td className="px-4 py-3">
                                            <p className="text-ink">{p.referred?.name || "—"}</p>
                                            <p className="text-[11px] text-[#A3A3A3]">{p.referred?.email}</p>
                                        </td>
                                        <td className="px-4 py-3 text-[#525252]">
                                            <p className="text-ink text-sm truncate max-w-[240px]">
                                                {p.job?.title || "—"}
                                            </p>
                                            {p.job?.code && (
                                                <span
                                                    data-testid={`payout-jobcode-${p.id}`}
                                                    className="mt-0.5 font-mono text-[10px] tracking-wider bg-[#F5F5F5] text-[#525252] px-1.5 py-0.5 inline-block"
                                                >
                                                    {p.job.code}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 font-bold text-ink whitespace-nowrap">
                                            <div className="flex items-center gap-2">
                                                <span>{sym}{p.amount.toLocaleString("en-IN")}</span>
                                                <span
                                                    data-testid={`payout-source-${p.id}`}
                                                    title={
                                                        p.amount_source === "job"
                                                            ? "Amount overridden on this specific requirement"
                                                            : "Amount inherited from the workspace default"
                                                    }
                                                    className={`text-[9px] font-black uppercase tracking-[0.15em] px-1.5 py-0.5 whitespace-nowrap ${
                                                        p.amount_source === "job"
                                                            ? "bg-gradient-to-r from-[#FFFBEB] to-[#FEF3C7] text-[#92400E] border border-[#F59E0B]/50"
                                                            : "bg-[#F5F5F5] text-[#525252] border border-[#E5E5E5]"
                                                    }`}
                                                >
                                                    {p.amount_source === "job" ? "Per-job" : "Workspace"}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span
                                                className={`text-[10px] font-black uppercase tracking-wider px-2 py-1 ${
                                                    p.status === "paid"
                                                        ? "bg-[#ECFDF5] text-[#059669]"
                                                        : "bg-[#FEF3C7] text-[#B45309]"
                                                }`}
                                            >
                                                {p.status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            {p.status === "pending" ? (
                                                <button
                                                    onClick={() => markPaid(p.id)}
                                                    disabled={busy === p.id}
                                                    data-testid={`payout-mark-paid-${p.id}`}
                                                    className="btn-gradient px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1 disabled:opacity-50"
                                                >
                                                    <Check size={12} weight="bold" /> Mark paid
                                                </button>
                                            ) : (
                                                <span className="text-[11px] text-[#A3A3A3]">
                                                    {p.paid_at && new Date(p.paid_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                </>
            )}
        </div>
    );
}
